import { useCallback, useEffect, useRef, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useLocation } from "react-router-dom";
import { NotebookPen } from "lucide-react";
import ManagerNotesPanel from "./ManagerNotesPanel";

const STORAGE_KEY = "ffa-notes-fab-pos";
const SIZE = 52;
const MARGIN = 12;

interface Pos {
  x: number;
  y: number;
}

const clampToViewport = (p: Pos): Pos => ({
  x: Math.min(Math.max(p.x, MARGIN), Math.max(MARGIN, window.innerWidth - SIZE - MARGIN)),
  y: Math.min(Math.max(p.y, MARGIN), Math.max(MARGIN, window.innerHeight - SIZE - MARGIN)),
});

const defaultPos = (): Pos => ({
  x: Math.max(MARGIN, window.innerWidth - SIZE - 16),
  y: Math.max(MARGIN, window.innerHeight - SIZE - 120),
});

/**
 * Draggable floating access point to Manager Notes. Rendered on the dashboard
 * and every FPL Hub screen for signed-in managers.
 */
const FloatingNotesButton = () => {
  const { isSignedIn } = useUser();
  const location = useLocation();
  const [pos, setPos] = useState<Pos | null>(null);
  const [open, setOpen] = useState(false);
  const dragging = useRef(false);
  const moved = useRef(false);
  const offset = useRef<Pos>({ x: 0, y: 0 });

  const enabled =
    isSignedIn &&
    (location.pathname.startsWith("/dashboard") || location.pathname.startsWith("/fpl-hub"));

  useEffect(() => {
    if (!enabled) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as Pos) : null;
      setPos(clampToViewport(parsed && Number.isFinite(parsed.x) ? parsed : defaultPos()));
    } catch {
      setPos(defaultPos());
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const openHandler = () => setOpen(true);
    window.addEventListener("ffa:open-manager-notes", openHandler);
    return () => window.removeEventListener("ffa:open-manager-notes", openHandler);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const onResize = () => setPos((p) => (p ? clampToViewport(p) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [enabled]);

  const endDrag = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    setPos((p) => {
      if (p) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
        } catch {
          /* ignore */
        }
      }
      return p;
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const onMove = (clientX: number, clientY: number) => {
      if (!dragging.current) return;
      moved.current = true;
      setPos(clampToViewport({ x: clientX - offset.current.x, y: clientY - offset.current.y }));
    };
    const mouseMove = (e: MouseEvent) => onMove(e.clientX, e.clientY);
    const touchMove = (e: TouchEvent) => {
      if (!dragging.current) return;
      const t = e.touches[0];
      if (t) {
        e.preventDefault();
        onMove(t.clientX, t.clientY);
      }
    };
    window.addEventListener("mousemove", mouseMove);
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchmove", touchMove, { passive: false });
    window.addEventListener("touchend", endDrag);
    return () => {
      window.removeEventListener("mousemove", mouseMove);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("touchmove", touchMove);
      window.removeEventListener("touchend", endDrag);
    };
  }, [enabled, endDrag]);

  if (!enabled || !pos) return null;

  const startDrag = (clientX: number, clientY: number) => {
    dragging.current = true;
    moved.current = false;
    offset.current = { x: clientX - pos.x, y: clientY - pos.y };
  };

  return (
    <>
      <button
        type="button"
        aria-label="Open Manager Notes"
        onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
        onTouchStart={(e) => {
          const t = e.touches[0];
          if (t) startDrag(t.clientX, t.clientY);
        }}
        onClick={() => {
          if (!moved.current) setOpen(true);
        }}
        style={{ left: pos.x, top: pos.y, width: SIZE, height: SIZE }}
        className="fixed z-40 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 border border-primary-foreground/20 flex items-center justify-center touch-none active:scale-95 transition-transform"
      >
        <NotebookPen className="w-5 h-5" />
      </button>
      <ManagerNotesPanel open={open} onOpenChange={setOpen} />
    </>
  );
};

export default FloatingNotesButton;
